import React, { useEffect } from 'react'
import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import type {
  SortOrder,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/es/table/interface'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { OwnerMemberListItemDto } from 'api-client'
import { getPagePath } from 'router/utils'
import { ListSearch } from 'shared/components/listing/ListSearch'
import { getUiDate, getUiDateShortTime } from 'shared/dates/formatters'
import { useDebounce } from 'shared/hooks/useDebounce'
import { OwnerMembersSortBy, OwnerMembersStore } from 'store/owner-members.store'

const SORTABLE_KEYS: OwnerMembersSortBy[] = ['registeredAt', 'balance', 'lastVisitAt']
const NEGATIVE_BALANCE_COLOR = '#cf1322'

export const OwnerMembersTableFeature = observer(() => {
  useEffect(() => {
    OwnerMembersStore.reset()
    OwnerMembersStore.load()
  }, [])

  const sortOrderFor = (key: OwnerMembersSortBy): SortOrder => {
    if (OwnerMembersStore.sortBy !== key) return null
    return OwnerMembersStore.sortDir === 'asc' ? 'ascend' : 'descend'
  }

  const columns: ColumnsType<OwnerMemberListItemDto> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, item) => (
        <Link to={getPagePath(`owner/members/${item.memberId}`)} className="table-link">
          {`${item.firstName ?? ''} ${item.lastName ?? ''}`.trim()}
        </Link>
      ),
    },
    {
      title: 'Registered',
      key: 'registeredAt',
      dataIndex: 'registeredAt',
      sorter: true,
      sortOrder: sortOrderFor('registeredAt'),
      render: (value?: string) => getUiDate(value) || '—',
    },
    {
      title: 'Last visit',
      key: 'lastVisitAt',
      dataIndex: 'lastVisitAt',
      sorter: true,
      sortOrder: sortOrderFor('lastVisitAt'),
      render: (value?: string | null) => getUiDateShortTime(value ?? undefined) || '—',
    },
    {
      title: 'Balance',
      key: 'balance',
      dataIndex: 'balance',
      align: 'right',
      sorter: true,
      sortOrder: sortOrderFor('balance'),
      render: (value?: number) => (
        <strong style={{ color: (value ?? 0) < 0 ? NEGATIVE_BALANCE_COLOR : undefined }}>
          {value ?? 0}
        </strong>
      ),
    },
  ]

  const search = async (query: string) => {
    OwnerMembersStore.setSearch(query.trim())
    await OwnerMembersStore.load()
  }

  const debouncedSearch = useDebounce(search, 300)

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<OwnerMemberListItemDto> | SorterResult<OwnerMemberListItemDto>[],
    extra: TableCurrentDataSource<OwnerMemberListItemDto>
  ) => {
    if (extra.action === 'sort') {
      const current = Array.isArray(sorter) ? sorter[0] : sorter
      const key = current?.columnKey as OwnerMembersSortBy | undefined
      if (current?.order && key && SORTABLE_KEYS.includes(key)) {
        OwnerMembersStore.setSort(key, current.order === 'ascend' ? 'asc' : 'desc')
      } else {
        // Третий клик по колонке снимает сортировку — возвращаемся к сортировке по умолчанию.
        OwnerMembersStore.setSort()
      }
    } else if (extra.action === 'paginate') {
      OwnerMembersStore.setPage(
        pagination.current ?? 1,
        pagination.pageSize ?? OwnerMembersStore.pageSize
      )
    }
    OwnerMembersStore.load()
  }

  return (
    <div>
      <ListSearch setValue={debouncedSearch} placeholder="Search by name" />
      <Table
        rowKey="memberId"
        columns={columns}
        dataSource={OwnerMembersStore.items.slice()}
        loading={OwnerMembersStore.loadEffect.isLoading}
        onChange={handleTableChange}
        pagination={{
          current: OwnerMembersStore.pageNumber,
          pageSize: OwnerMembersStore.pageSize,
          total: OwnerMembersStore.totalCount,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          position: ['bottomCenter'],
        }}
      />
    </div>
  )
})
